import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, FileDown, ImageDown, Printer, PauseCircle, Ban, PlayCircle, RefreshCw, CheckCircle2, XCircle } from "lucide-react";
import { api, errMsg, downloadFile } from "@/lib/api";
import { fmtDateTime, LICENSE_STATUS } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Loading, useSettings } from "@/components/common";
import { Button } from "@/components/ui/button";
import { LicenseCard } from "@/components/LicenseCard";
import { ApproveDialog, ReasonDialog } from "@/components/LicenseDialogs";

const ACTIONS = {
  suspend: { title: "Sospendi licenza", label: "Sospendi", danger: true, from: ["active"], icon: PauseCircle, ok: "Licenza sospesa" },
  revoke: { title: "Revoca licenza", label: "Revoca definitivamente", danger: true, from: ["active", "suspended", "expired"], icon: Ban, ok: "Licenza revocata" },
  reactivate: { title: "Riattiva licenza", label: "Riattiva", from: ["suspended"], icon: PlayCircle, ok: "Licenza riattivata" },
  "regenerate-qr": { title: "Rigenera QR code", label: "Rigenera", from: ["active", "suspended", "expired"], icon: RefreshCw, ok: "Nuovo QR generato: il precedente non è più valido",
    description: "Il QR code attuale (e ogni sua copia) smetterà immediatamente di funzionare." },
  reject: { title: "Respingi richiesta", label: "Respingi", danger: true, from: ["pending"], icon: XCircle, ok: "Richiesta respinta" },
};

export default function LicenseDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const settings = useSettings();
  const [lic, setLic] = useState(null);
  const [action, setAction] = useState(null);
  const [approve, setApprove] = useState(false);
  const load = () => api.get(`/licenses/${id}`).then((r) => setLic(r.data)).catch((e) => toast.error(errMsg(e)));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [id]);
  if (!lic) return <Loading />;
  const issued = !!lic.license_number && lic.status !== "pending";
  const isAdmin = user.role === "admin";
  const act = ACTIONS[action];

  const dl = (path, name) => downloadFile(path, name).catch((e) => toast.error(errMsg(e)));
  return (
    <div>
      <button onClick={() => navigate(-1)} className="mb-4 inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900 no-print" data-testid="back-btn"><ArrowLeft className="h-4 w-4" />Indietro</button>
      <PageHeader eyebrow="Tessera digitale" title={lic.license_number || "Richiesta di licenza"} subtitle={`${lic.athlete_name} · ${lic.discipline_name} · ${LICENSE_STATUS[lic.status].label}`}>
        {issued && <>
          <Button variant="outline" onClick={() => dl(`/licenses/${id}/pdf`, `Licenza-${lic.license_number}.pdf`)} data-testid="license-pdf-btn"><FileDown className="mr-2 h-4 w-4" />Scarica PDF</Button>
          <Button variant="outline" onClick={() => dl(`/licenses/${id}/qr.png`, `QR-${lic.license_number}.png`)} data-testid="license-qr-download-btn"><ImageDown className="mr-2 h-4 w-4" />Scarica QR</Button>
          <Button variant="outline" onClick={() => window.print()} data-testid="license-print-btn"><Printer className="mr-2 h-4 w-4" />Stampa</Button>
        </>}
      </PageHeader>
      <div className="grid gap-6 lg:grid-cols-[1.4fr_1fr]">
        <div><LicenseCard lic={lic} settings={settings} /></div>
        <div className="space-y-4 no-print">
          {isAdmin && (
            <div className="card-surface p-5">
              <div className="mb-3 font-display text-base font-semibold">Azioni sede nazionale</div>
              <div className="flex flex-wrap gap-2">
                {lic.status === "pending" && <Button onClick={() => setApprove(true)} className="bg-emerald-600 hover:bg-emerald-700" data-testid="license-approve-btn"><CheckCircle2 className="mr-2 h-4 w-4" />Approva e rilascia</Button>}
                {Object.entries(ACTIONS).filter(([, a]) => a.from.includes(lic.status)).map(([k, a]) => (
                  <Button key={k} variant="outline" onClick={() => setAction(k)} className={a.danger ? "border-red-200 text-red-700 hover:bg-red-50" : ""} data-testid={`license-action-${k}-btn`}>
                    <a.icon className="mr-2 h-4 w-4" />{a.title}
                  </Button>
                ))}
                {lic.status === "revoked" && <div className="text-sm text-slate-500">Licenza revocata: nessuna ulteriore azione disponibile.</div>}
              </div>
            </div>
          )}
          {user.role === "sector_manager" && lic.status === "pending" && (
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">Richiesta in attesa di approvazione da parte della sede nazionale.</div>
          )}
          <div className="card-surface p-5">
            <div className="mb-3 font-display text-base font-semibold">Storico stati</div>
            <ol className="space-y-3" data-testid="license-history">
              {[...(lic.status_history || [])].reverse().map((h, i) => (
                <li key={i} className="border-l-2 border-slate-200 pl-3">
                  <div className="text-sm font-semibold text-slate-900">{LICENSE_STATUS[h.status]?.label}</div>
                  <div className="text-xs text-slate-500">{fmtDateTime(h.at)} · {h.by}</div>
                  {h.reason && <div className="text-sm text-slate-600">{h.reason}</div>}
                </li>
              ))}
            </ol>
          </div>
        </div>
      </div>
      <ApproveDialog open={approve} onOpenChange={setApprove} lic={lic} onDone={load} />
      {act && (
        <ReasonDialog open={!!action} onOpenChange={(v) => !v && setAction(null)} title={act.title} description={act.description}
          confirmLabel={act.label} danger={act.danger}
          onConfirm={async (reason) => { await api.post(`/licenses/${id}/${action}`, { reason }); toast.success(act.ok); load(); }} />
      )}
    </div>
  );
}
